import { getRuntimeApiBase } from "./runtime-api-base";

export const PUBLIC_MAC_DOWNLOAD_URL =
  "https://fintheon.fly.dev/api/desktop/update/download/latest?arch=arm64";

export interface DesktopDownload {
  assetName: string;
  downloadUrl: string;
  releaseUrl: string;
  size: number | null;
  version: string;
}

export async function fetchLatestDesktopDownload(
  fetcher: typeof fetch = fetch,
): Promise<DesktopDownload> {
  const endpoint = new URL("/api/desktop/update/latest", apiBaseForBrowser());
  endpoint.searchParams.set("platform", "darwin");
  endpoint.searchParams.set("arch", "arm64");

  const response = await fetcher(endpoint, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error("Desktop download is temporarily unavailable.");

  return parseDesktopDownload(await response.json());
}

export function parseDesktopDownload(value: unknown): DesktopDownload {
  if (!isRecord(value))
    throw new Error("Desktop download response is invalid.");

  const assetName = readString(value.assetName);
  const downloadUrl = readHttpsUrl(value.downloadUrl);
  const releaseUrl = readHttpsUrl(value.releaseUrl);
  const version = readString(value.version);
  if (!assetName || !downloadUrl || !releaseUrl || !version) {
    throw new Error("Desktop download response is incomplete.");
  }
  if (!/^Fintheon-\d+\.\d+\.\d+-(arm64|x64|universal)\.dmg$/.test(assetName)) {
    throw new Error("Desktop download asset is unsupported.");
  }

  return {
    assetName,
    downloadUrl,
    releaseUrl,
    size: typeof value.size === "number" ? value.size : null,
    version,
  };
}

export function formatDesktopDownloadSize(size: number | null): string | null {
  if (!size || size <= 0) return null;
  return `${(size / 1_000_000).toFixed(0)} MB`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readHttpsUrl(value: unknown): string | null {
  const raw = readString(value);
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function apiBaseForBrowser(): string {
  const runtimeBase = getRuntimeApiBase();
  if (runtimeBase) return runtimeBase;
  if (typeof window !== "undefined") return window.location.origin;
  return "http://localhost:8080";
}
